/**
 * Objectives Page - Constants and Utilities
 */

import { Timestamp } from 'firebase/firestore';
import type { Objective } from '../../models';

/**
 * Mock objectives data (10 objectives)
 */
export const MOCK_OBJECTIVES: Objective[] = [
  {
    id: 'obj-1',
    title: 'Aumentar receita em 30%',
    description: 'Expandir base de clientes e aumentar ticket médio',
    type: 'committed',
    level: 1,
    cycleId: 'cycle-1',
    ownerId: 'user-1',
    ownerName: 'João Silva',
    ownerEmail: 'joao@example.com',
    allowedRoles: ['admin', 'manager'],
    status: 'active',
    progress: 65,
    healthStatus: 'on_track',
    keyResultCount: 3,
    path: '/obj-1',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-15')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-20')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-1' },
  },
  {
    id: 'obj-2',
    title: 'Melhorar satisfação do cliente para 90%',
    description: 'Implementar programa de feedback e melhorias contínuas',
    type: 'committed',
    level: 1,
    cycleId: 'cycle-1',
    ownerId: 'user-2',
    ownerName: 'Maria Santos',
    ownerEmail: 'maria@example.com',
    allowedRoles: ['admin', 'manager'],
    status: 'active',
    progress: 45,
    healthStatus: 'at_risk',
    keyResultCount: 3,
    path: '/obj-2',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-10')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-18')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-2' },
  },
  {
    id: 'obj-3',
    title: 'Lançar 3 novos produtos',
    description: 'Desenvolver e lançar produtos inovadores no mercado',
    type: 'aspirational',
    level: 2,
    cycleId: 'cycle-1',
    ownerId: 'user-3',
    ownerName: 'Pedro Costa',
    ownerEmail: 'pedro@example.com',
    parentObjectiveId: 'obj-1',
    parentObjectivePath: '/obj-1',
    allowedRoles: ['admin', 'manager', 'team_lead'],
    status: 'active',
    progress: 33,
    healthStatus: 'at_risk',
    keyResultCount: 4,
    path: '/obj-1/obj-3',
    ancestorIds: ['obj-1'],
    createdAt: Timestamp.fromDate(new Date('2025-01-12')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-22')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-3' },
  },
  {
    id: 'obj-4',
    title: 'Reduzir churn em 20%',
    description: 'Implementar estratégias de retenção de clientes',
    type: 'committed',
    level: 2,
    cycleId: 'cycle-1',
    ownerId: 'user-2',
    ownerName: 'Maria Santos',
    ownerEmail: 'maria@example.com',
    parentObjectiveId: 'obj-2',
    parentObjectivePath: '/obj-2',
    allowedRoles: ['admin', 'manager'],
    status: 'active',
    progress: 55,
    healthStatus: 'on_track',
    keyResultCount: 3,
    path: '/obj-2/obj-4',
    ancestorIds: ['obj-2'],
    createdAt: Timestamp.fromDate(new Date('2025-01-14')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-21')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-2' },
  },
  {
    id: 'obj-5',
    title: 'Automatizar 50% dos processos manuais',
    description: 'Implementar ferramentas de automação e RPA',
    type: 'committed',
    level: 3,
    cycleId: 'cycle-1',
    ownerId: 'user-5',
    ownerName: 'Carlos Souza',
    ownerEmail: 'carlos@example.com',
    allowedRoles: ['admin', 'manager', 'team_lead'],
    status: 'active',
    progress: 40,
    healthStatus: 'at_risk',
    keyResultCount: 3,
    path: '/obj-5',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-08')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-19')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-5' },
  },
  {
    id: 'obj-6',
    title: 'Contratar 15 novos talentos',
    description: 'Expandir equipe com profissionais qualificados',
    type: 'committed',
    level: 3,
    cycleId: 'cycle-1',
    ownerId: 'user-6',
    ownerName: 'Fernanda Alves',
    ownerEmail: 'fernanda@example.com',
    allowedRoles: ['admin', 'hr'],
    status: 'active',
    progress: 60,
    healthStatus: 'on_track',
    keyResultCount: 2,
    path: '/obj-6',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-05')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-23')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-6' },
  },
  {
    id: 'obj-7',
    title: 'Implementar programa de capacitação',
    description: 'Treinar equipe em novas tecnologias e metodologias',
    type: 'aspirational',
    level: 4,
    cycleId: 'cycle-1',
    ownerId: 'user-7',
    ownerName: 'Lucas Pereira',
    ownerEmail: 'lucas@example.com',
    allowedRoles: ['admin'],
    status: 'draft',
    progress: 10,
    healthStatus: 'off_track',
    keyResultCount: 2,
    path: '/obj-7',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-25')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-25')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-7' },
  },
  {
    id: 'obj-8',
    title: 'Migrar infraestrutura para cloud',
    description: 'Modernizar arquitetura e reduzir custos',
    type: 'committed',
    level: 3,
    cycleId: 'cycle-1',
    ownerId: 'user-5',
    ownerName: 'Carlos Souza',
    ownerEmail: 'carlos@example.com',
    parentObjectiveId: 'obj-5',
    parentObjectivePath: '/obj-5',
    allowedRoles: ['admin', 'tech_lead'],
    status: 'active',
    progress: 70,
    healthStatus: 'on_track',
    keyResultCount: 4,
    path: '/obj-5/obj-8',
    ancestorIds: ['obj-5'],
    createdAt: Timestamp.fromDate(new Date('2025-01-11')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-24')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-5' },
  },
  {
    id: 'obj-9',
    title: 'Expandir presença em redes sociais',
    description: 'Aumentar engajamento e seguidores em 100%',
    type: 'aspirational',
    level: 4,
    cycleId: 'cycle-1',
    ownerId: 'user-9',
    ownerName: 'Juliana Rocha',
    ownerEmail: 'juliana@example.com',
    allowedRoles: ['admin', 'marketing'],
    status: 'active',
    progress: 25,
    healthStatus: 'at_risk',
    keyResultCount: 3,
    path: '/obj-9',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-16')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-17')),
    completedAt: null,
    _metadata: { version: 1, lastModifiedBy: 'user-9' },
  },
  {
    id: 'obj-10',
    title: 'Reduzir tempo médio de entrega em 30%',
    description: 'Otimizar processos e logística',
    type: 'committed',
    level: 2,
    cycleId: 'cycle-1',
    ownerId: 'user-10',
    ownerName: 'Ricardo Mendes',
    ownerEmail: 'ricardo@example.com',
    allowedRoles: ['admin', 'operations'],
    status: 'completed',
    progress: 100,
    healthStatus: 'on_track',
    keyResultCount: 5,
    path: '/obj-10',
    ancestorIds: [],
    parentObjectiveId: null,
    parentObjectivePath: null,
    createdAt: Timestamp.fromDate(new Date('2025-01-01')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-26')),
    completedAt: Timestamp.fromDate(new Date('2025-01-26')),
    _metadata: { version: 1, lastModifiedBy: 'user-10' },
  },
];

/**
 * Filter objectives by criteria
 */
export function filterObjectives(
  objectives: Objective[],
  filters: { level?: number; status?: string; searchTerm?: string }
): Objective[] {
  return objectives.filter((obj) => {
    if (filters.level && obj.level !== filters.level) return false;
    if (filters.status && obj.status !== filters.status) return false;
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      const matchesTitle = obj.title.toLowerCase().includes(term);
      const matchesDescription = obj.description?.toLowerCase().includes(term);
      if (!matchesTitle && !matchesDescription) return false;
    }
    return true;
  });
}

/**
 * Level filter options
 */
export const LEVEL_OPTIONS = [
  { value: 1, label: 'Organizacional' },
  { value: 2, label: 'Área' },
  { value: 3, label: 'Time' },
  { value: 4, label: 'Individual' },
];

/**
 * Status filter options
 */
export const STATUS_OPTIONS = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'active', label: 'Ativo' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
];
